import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {
  Box,
  CSSObject,
  IconButton,
  ListItemButton,
  styled,
  Theme,
  useMediaQuery
} from '@mui/material';
import { MenuBook } from '@mui/icons-material';
import { getDocumentationPath, i18n } from 'i18n';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import { useTheme } from '@mui/material/styles';
import {
  useNavigate,
  useMatch
} from 'react-router-dom';
import {
  groupByProp,
  lastIndex
} from 'utils';
import LogoOpen from './logo.png';
import { TopLevelMenuItem } from './types';
import { DRAWER_WIDTH } from './utils';

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const StyledDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': {
            ...openedMixin(theme),
            display: 'flex',
            flexDirection: 'column',
          },
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': {
            ...closedMixin(theme),
            display: 'flex',
            flexDirection: 'column',
          },
        },
      },
    ],
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  background: '#000',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const menuByGroup = groupByProp<TopLevelMenuItem>('group');

interface MenuItemProps {
  menuItem: TopLevelMenuItem;
  open: boolean;
  notMenuItem: boolean
}

const MenuItem = ({ menuItem, open, notMenuItem }: MenuItemProps) => {
  const match = notMenuItem ? false : useMatch(menuItem.path);

  const navigate = useNavigate();

  const handleMenuSelect = () => {
    if (menuItem.external) {
      window.open(menuItem.path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(menuItem.path);
    }
  };
  return (
    <ListItem>
      <ListItemButton
        onClick={handleMenuSelect}
        selected={!!match}
        sx={[
          {
            minHeight: 48,
            px: 2.5,
          },
          open
            ? {
              justifyContent: 'initial',
            }
            : {
              justifyContent: 'center',
            },
        ]}
      >
        <ListItemIcon
          sx={[
            {
              minWidth: 0,
              justifyContent: 'center',
            },
            open
              ? {
                mr: 3,
              }
              : {
                mr: 'auto',
              },
          ]}
        >
          <menuItem.icon />
        </ListItemIcon>
        <ListItemText
          primary={menuItem.display}
          sx={[
            open
              ? { opacity: 1 }
              : {
                opacity: 0,
                width: 0,
                overflow: 'hidden',
              },
          ]}
        />
      </ListItemButton>
    </ListItem>
  );
};

interface MenuProps {
  closeMenu: () => void;
  open: boolean;
  menu: TopLevelMenuItem[];
}

interface SideDrawerProps extends MenuProps {
  variant: 'permanent' | 'temporary';
  disabled: boolean;
}

const SideDrawer = ({ closeMenu, open, menu, variant, disabled }: SideDrawerProps) => {
  const groupedMenu = menuByGroup(menu);
  const menuGroups = Object.keys(groupedMenu);

  const openAndEnabled = open && !disabled;

  const documentationMenuItem: TopLevelMenuItem = {
    display: i18n._('Software Guide'),
    path: getDocumentationPath(i18n.locale),
    icon: MenuBook,
  };

  return (
    <StyledDrawer anchor="left" variant={variant} open={openAndEnabled} onClose={closeMenu}>
      <DrawerHeader>
        <Box sx={{ width: '100%', p: 1 }}>
          <img width="100%" src={LogoOpen} alt="Mine Smart Logo" />
        </Box>
        <IconButton onClick={closeMenu} size="small">
          <ChevronLeftIcon />
        </IconButton>
      </DrawerHeader>

      <Divider />

      {/* Scrollable menu list */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {menuGroups.map((group, index) => {
          const listSubHeader = group !== 'undefined'
            ? <ListSubheader>{open ? group : undefined}</ListSubheader>
            : undefined;

          return (
            <Box key={group ?? 'no-group'}>
              <List subheader={listSubHeader}>
                {groupedMenu[group].map((menuItem) => (
                  <MenuItem key={menuItem.path} menuItem={menuItem} open={open} notMenuItem={false} />
                ))}
              </List>
              {lastIndex(menuGroups) !== index && <Divider />}
            </Box>
          );
        })}
      </Box>

      {/* Fixed bottom Documentation link */}
      <Box sx={{ mt: 'auto', mb: 1 }}>
        <MenuItem key={documentationMenuItem.path} menuItem={documentationMenuItem} open={open} notMenuItem />
      </Box>
    </StyledDrawer>
  );
};

const Menu = (props: MenuProps) => {
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <>
      <SideDrawer {...props} variant="permanent" disabled={!isLarge} />
      <SideDrawer {...props} variant="temporary" disabled={isLarge} />
    </>
  );
};

export default Menu;
